import { Pooling, PoolingMode, PoolingType } from '@resistapp/common/api-types';
import { GetGroup } from '@resistapp/common/assays';
import { safeAssert } from '@resistapp/common/assert-utils';
import {
  SampleTypeGroup,
  SmplPntGroup,
  getComparableSamplingPointGroups,
} from '@resistapp/common/comparable-sample-groups';
import { ensureLocalMidnight, ensureLocalStartOfMonth, friendlyMonth } from '@resistapp/common/friendly';
import { getPoolingKey, poolSampleSamplingPoints } from '@resistapp/common/pool-samples';
import {
  SampleSubType,
  SampleType,
  isAfterSample,
  isBeforeSample,
} from '@resistapp/common/sampling-point-types';
import { RequiredNonNullableFields } from '@resistapp/common/type-utils';
import { arePropertiesPresentInAllFullSamples } from '@resistapp/common/typeguards';
import {
  FullAbundance,
  FullSample,
  FullSamplesByUID,
  MetricMode,
  PooledSplPntHack,
  Process,
  ProcessMode,
  SamplingPoint,
  getOriginalSamplingPointId,
} from '@resistapp/common/types';
import { flattenSamplesByUID } from '@resistapp/common/utils';
import { chain, intersection, isNil, keyBy, keys, pickBy, uniq } from 'lodash';
import { WithAbundances } from '../../../common/statistics/resistance-index';
import { DateDatum } from './research-plot-data';

export type TimeSeriesOfOverviewDatums = OverviewDatum[]; // inner array in trendData, indexed by time (month), ie. singleSiteOrAreaTimeSeries[time]
export type OverviewLineData = TimeSeriesOfOverviewDatums[]; // outer array in trendData, indexed by iSiteOrArea and then time, ie. trendData[iSiteOrArea][time] = trendData[iSiteOrArea][time]
export type SmplPntSeriesOfOverviewDatums = OverviewDatum[]; // overview datums for each site, vertically picked from a single time or latest timepoints, ie. mapData[iSiteOrArea] = trendData[iSiteOrArea][trendData[iSiteOrArea].length - 1]

export type FullSampleWithTime = RequiredNonNullableFields<FullSample, 'time'>;

export interface OverviewDatum extends DateDatum {
  samplingPoint: SamplingPoint | PooledSplPntHack;
  beforeAbundances: FullAbundance[] | undefined;
  afterAbundances: FullAbundance[] | undefined;
  samplingPointAfter?: SamplingPoint | PooledSplPntHack;
  processId?: number;
  processStepPointIds?: number[];
  processFlowIds?: number[];
  processStepSummaries?: ProcessStepSummary[];
  uniqueDates: Date[];
}

export interface ProcessStepSummary {
  samplingPointId: number;
  name: string;
  subtype: SampleSubType | null;
  beforeAbundances: FullAbundance[];
  afterAbundances: FullAbundance[];
}

export interface OverviewDatumWithQuartiles extends OverviewDatum {
  firstQuartile?: number;
  thirdQuartile?: number;
}

// Base unsupported types (always excluded)
const baseUnsupportedOverviewSampleTypeGroups: SampleTypeGroup[] = [
  // AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS,
  SampleType.CONTROL,
  SampleType.STOOL, // Used as control in some projects
];

export function getUnsupportedOverviewSampleTypeGroups(options?: { allowFood?: boolean }): SampleTypeGroup[] {
  if (options?.allowFood) {
    return baseUnsupportedOverviewSampleTypeGroups;
  }
  return [...baseUnsupportedOverviewSampleTypeGroups, SampleType.FOOD];
}

// Legacy export for backward compatibility (defaults to excluding FOOD)
export const unsuportedOverviewSampleTypeGroups: SampleTypeGroup[] = [
  ...baseUnsupportedOverviewSampleTypeGroups,
  SampleType.FOOD,
];

export function getBeforeOrAfterAbundances<T extends WithAbundances>(datum: T, processMode: ProcessMode) {
  const step: ProcessMode.AFTER | ProcessMode.BEFORE =
    processMode === ProcessMode.BEFORE || processMode === ProcessMode.AFTER ? processMode : ProcessMode.BEFORE;
  const key = step === ProcessMode.BEFORE ? ('beforeAbundances' as const) : ('afterAbundances' as const);
  const abundances = datum[key];
  return abundances;
}

export function getComparableSmplPntGroupsForOverview(
  samplesByUID: FullSamplesByUID,
  metricMode: MetricMode,
  processes?: Process[],
): SmplPntGroup[] {
  const flatSamples = flattenSamplesByUID(samplesByUID);
  const uniqueSamplingPoints = chain(flatSamples)
    .map(sample => sample.samplingPoint)
    .uniqBy(samplingPoint => samplingPoint.id)
    .value();
  const samplingPointById: Record<number, SamplingPoint | undefined> = keyBy(
    uniqueSamplingPoints,
    samplingPoint => samplingPoint.id,
  );
  const hasFoodStepInAnyProcess = !!processes?.some(process =>
    (process.samplingPointIds || []).some(samplingPointId => samplingPointById[samplingPointId]?.type === SampleType.FOOD),
  );
  const unsupported = getUnsupportedOverviewSampleTypeGroups({
    allowFood: hasFoodStepInAnyProcess,
  });
  const comparableGroups = getComparableSamplingPointGroups(uniqueSamplingPoints, metricMode);
  return comparableGroups.filter(group => !unsupported.includes(group.type));
}

/*
 * Get relevant samples for overview and drop admin levels that are not present in all samples
 *
 * Overview is designed for (and may make biological sense only for) selected sample types
 */
export function getSupportedSamplesWithConsistentAdminLevels(
  samplesByUID: FullSamplesByUID,
  envGroups: SmplPntGroup[],
): FullSample[] {
  const supportedSamplingPointIds = new Set(
    envGroups.flatMap(envGroup => envGroup.smplPnts).map(smplPnt => smplPnt.id),
  );
  const flatSamples = flattenSamplesByUID(samplesByUID);
  const flatSupportedSamples = flatSamples.filter(
    sample =>
      !isNil(sample.samplingPoint.inferredLat) &&
      !isNil(sample.samplingPoint.inferredLon) &&
      !isNil(sample.samplingPoint.lat) &&
      !isNil(sample.samplingPoint.lon) &&
      !isNil(sample.time) &&
      supportedSamplingPointIds.has(sample.samplingPoint.id),
  );
  const levelsInAllSamples = new Set(
    chain(flatSupportedSamples)
      .map(s => s.samplingPoint.adminLevels || {})
      .map(levels => keys(levels).map(level => +level))
      .reduce((acc, levels) => intersection(acc, levels))
      .value(),
  );
  const supportedSamplesWithConsistentLevels = flatSupportedSamples.map(sample => ({
    ...sample,
    samplingPoint: {
      ...sample.samplingPoint,
      adminLevels: pickBy(sample.samplingPoint.adminLevels, datum => levelsInAllSamples.has(datum.level)),
    },
  }));
  return supportedSamplesWithConsistentLevels;
}

/*
 * Build BoxDatum series (arrays), where each series contains time series BoxDatums for one overview sampling point / location (line)
 */
export function buildOverviewLineData(
  samples: FullSample[],
  selectedSamplingPointIds: Set<number>,
  processMode: ProcessMode,
  pooling: Pooling | undefined,
  getGroup: GetGroup,
  processes?: Process[],
): OverviewLineData {
  if (!samples.length) {
    throw new Error('No supported samples available');
  }
  const samplesHaveTimes = arePropertiesPresentInAllFullSamples(samples, 'time');
  if (!samplesHaveTimes) {
    throw new Error('Supported samples unexpectedly missing time');
  }
  const samplesHaveLocations = samples.every(
    sample => !isNil(sample.samplingPoint.inferredLat) && !isNil(sample.samplingPoint.inferredLon),
  );
  if (!samplesHaveLocations) {
    throw new Error('Supported samples unexpectedly missing location');
  }

  // After validation, we can safely cast to FullSampleWithTime
  const samplesWithTime = samples as FullSampleWithTime[];
  const filteredProcesses = processes ? filterProcessesForVisibleSamples(processes, samplesWithTime) : undefined;
  const samplingPointById = keyBy(samplesWithTime.map(sample => sample.samplingPoint), samplingPoint => samplingPoint.id);
  const processMetadataContext = buildProcessMetadataContext(
    filteredProcesses,
    samplingPointById,
    pooling?.type === PoolingType.SITE && !!filteredProcesses,
  );

  // Before pooling by admin level, we have to combine site samples
  const maybeSitePooledSamples =
    pooling?.type === PoolingType.SITE_AND_ADMIN_LEVEL
      ? poolSampleSamplingPointsForOverview(
          samplesWithTime,
          {
            type: PoolingType.SITE,
            mode: PoolingMode.THROW_MISSING,
          },
          filteredProcesses,
        )
      : samplesWithTime;

  // Proceed with primary pooling
  const maybePooledSamples = poolSampleSamplingPointsForOverview(maybeSitePooledSamples, pooling, filteredProcesses);

  return chain(maybePooledSamples)
    .groupBy(sample => sample.samplingPoint.id)
    .mapValues(siteSamples => {
      return buildSingleOverviewLineSeries(
        siteSamples,
        selectedSamplingPointIds,
        processMode,
        getGroup,
        processMetadataContext,
      );
    })
    .values()
    .value();
}

type ProcessMetadataContext = {
  processById: Record<number, Process | undefined>;
  processIdBySamplingPointId: Record<number, number | undefined>;
  samplingPointById: Record<number, SamplingPoint | PooledSplPntHack | undefined>;
  isProcessGroupLevel: boolean;
};

function buildProcessMetadataContext(
  processes: Process[] | undefined,
  samplingPointById: Record<number, SamplingPoint | PooledSplPntHack | undefined>,
  isProcessGroupLevel: boolean,
): ProcessMetadataContext | undefined {
  if (!processes?.length) {
    return undefined;
  }

  const processIdBySamplingPointId: Record<number, number | undefined> = chain(processes)
    .flatMap(process =>
      (process.samplingPointIds || []).map(samplingPointId => ({
        samplingPointId,
        processId: process.id,
      })),
    )
    .groupBy(entry => entry.samplingPointId)
    .mapValues(entries =>
      chain(entries)
        .map(entry => entry.processId)
        .sortBy()
        .first()
        .value(),
    )
    .value();

  return {
    processById: keyBy(processes, process => process.id),
    processIdBySamplingPointId,
    samplingPointById,
    isProcessGroupLevel,
  };
}

function poolSampleSamplingPointsForOverview(
  samples: FullSampleWithTime[],
  pooling: Pooling | undefined,
  processes: Process[] | undefined,
): FullSampleWithTime[] {
  // Special case checks
  if (!pooling) {
    return samples;
  } else if (pooling.type !== PoolingType.SITE_AND_ADMIN_LEVEL && pooling.type !== PoolingType.SITE) {
    throw Error('Only site and admin level pooling are supported in the overview');
  }

  // Combine sampling points so that samples with the same pooling criteria share the same location metadata
  const pooledSamples =
    pooling.type === PoolingType.SITE && processes
      ? poolSampleSamplingPoints(samples, pooling, buildProcessKeyGetter(processes))
      : poolSampleSamplingPoints(samples, pooling, sample => getPoolingKey(sample, pooling));

  return pooledSamples;
}

function buildProcessKeyGetter(processes: Process[]) {
  const processIdBySamplingPointId: Record<number, number | undefined> = chain(processes)
    .flatMap(process =>
      (process.samplingPointIds || []).map(samplingPointId => ({
        samplingPointId,
        processId: process.id,
      })),
    )
    .groupBy(entry => entry.samplingPointId)
    .mapValues(entries =>
      chain(entries)
        .map(entry => entry.processId)
        .sortBy()
        .first()
        .value(),
    )
    .value();

  return (sample: FullSampleWithTime) => {
    const processId = processIdBySamplingPointId[sample.samplingPoint.id];
    if (processId !== undefined) {
      return `process-${processId}`;
    }
    return `sampling-point-${sample.samplingPoint.id}`;
  };
}

function filterProcessesForVisibleSamples(processes: Process[], samples: FullSampleWithTime[]): Process[] {
  const visibleSamplingPointIdSet = new Set(samples.map(sample => sample.samplingPoint.id));

  return processes.filter(process => {
    const processSamplingPointIds = process.samplingPointIds ?? [];
    if (processSamplingPointIds.length === 0) {
      return false;
    }
    return processSamplingPointIds.some(id => visibleSamplingPointIdSet.has(id));
  });
}

// NOTE! Keep consistent with buildAfterSampleIdMapping
function buildSingleOverviewLineSeries(
  siteOrAreaSamples: FullSampleWithTime[],
  selectedSamplingPointIds: Set<number>,
  processMode: ProcessMode,
  getGroup: GetGroup,
  processMetadataContext?: ProcessMetadataContext,
): TimeSeriesOfOverviewDatums {
  return (
    chain(siteOrAreaSamples)
      // Always use monthly grouping
      .groupBy(sample => getSnappedLocalTimeFields(sample).snappedTime)
      .mapValues(siteOrAreaSamplesForTimePoint =>
        buildOverviewDatum(
          siteOrAreaSamplesForTimePoint,
          selectedSamplingPointIds,
          processMode,
          getGroup,
          processMetadataContext,
        ),
      )
      .values()
      .sortBy(datum => new Date(datum.date))
      .value()
  );
}

// NOTE! Keep consistent with buildAfterSampleIdMapping
function buildOverviewDatum(
  siteOrAreaSamplesForTimePoint: FullSampleWithTime[],
  selectedSamplingPointIds: Set<number>,
  processMode: ProcessMode,
  _getGroup: GetGroup,
  processMetadataContext?: ProcessMetadataContext,
): OverviewDatum {
  // Originally, all sites have a subtype or none have
  // Sample type pooling should only ever result in 1 or 2 uniq sub types for pooled samples
  // Area pooling results in undefined or one subtype -> one uniq
  // Site pooling results in 1 for process sites and 2 for pooled sites
  const numSubSamplingPointSubtypes = chain(siteOrAreaSamplesForTimePoint)
    .map(s => s.samplingPoint.subtype)
    .uniq()
    .value().length;
  const isIndividualSite = numSubSamplingPointSubtypes <= 2; // Non-process site, process site, or area containing only similar sites
  // TODO, areas currently aggregate accross before and after samples,
  // even when they could just aggregate over the selected process mode samples
  // We might want to change this, but let's hear some feedback first

  // Allow all process samples for individual process sites so that we can show secondary figures in the UI
  const selectedSamples = siteOrAreaSamplesForTimePoint.filter(
    s => isIndividualSite || keepSelectedSamplingPoints(s, selectedSamplingPointIds),
  );
  const beforeSamples = selectedSamples
    .filter(s => isBeforeSample(s) || (!isBeforeSample(s) && !isAfterSample(s)))
    .filter(_ => isIndividualSite || processMode !== ProcessMode.AFTER)
    .filter(
      s =>
        isIndividualSite ||
        !!selectedSamplingPointIds.has(
          (s.samplingPoint as PooledSplPntHack).originalSamplingPointId || s.samplingPoint.id,
        ),
    );
  const afterSamples = selectedSamples
    .filter(s => isAfterSample(s))
    .filter(_ => isIndividualSite || processMode !== ProcessMode.BEFORE)
    .filter(
      s =>
        isIndividualSite ||
        !!selectedSamplingPointIds.has(
          (s.samplingPoint as PooledSplPntHack).originalSamplingPointId || s.samplingPoint.id,
        ),
    );

  if (isIndividualSite && beforeSamples.length + afterSamples.length !== selectedSamples.length) {
    throw Error(`Before samples.length ${beforeSamples.length} + ${afterSamples.length} != ${selectedSamples.length}`);
  }

  const isSupportedSample = (s: FullSampleWithTime) =>
    !!selectedSamplingPointIds.has((s.samplingPoint as PooledSplPntHack).originalSamplingPointId || s.samplingPoint.id);
  const hasSupportedSamplingPoint = beforeSamples.find(isSupportedSample) || afterSamples.find(isSupportedSample);

  // Overview should only consider genuinely measured values. Filter out any modelled estimates (measured === false).
  const beforeAbundances: FullAbundance[] = hasSupportedSamplingPoint
    ? beforeSamples.flatMap(sampleLocal => getMeasuredAbundances(sampleLocal.abundances))
    : [];
  const afterAbundances: FullAbundance[] = hasSupportedSamplingPoint
    ? afterSamples.flatMap(sampleLocal => getMeasuredAbundances(sampleLocal.abundances))
    : [];

  const firstSample = siteOrAreaSamplesForTimePoint[0];
  const { snappedTime, formattedTime } = getSnappedLocalTimeFields(firstSample);

  const uniqueDates = chain(siteOrAreaSamplesForTimePoint)
    .map(sample => ensureLocalMidnight(sample.time))
    .uniqBy(date => date.getTime())
    .sortBy(date => date.getTime())
    .value();

  const processMetadata = buildProcessOverviewMetadata(
    siteOrAreaSamplesForTimePoint,
    processMetadataContext,
  );

  return {
    date: snappedTime,
    beforeAbundances,
    afterAbundances,
    label: formattedTime,
    samplingPoint: firstSample.samplingPoint,
    samplingPointAfter: afterSamples[0]?.samplingPoint,
    ...processMetadata,
    uniqueDates,
  };
}

function getMeasuredAbundances(abundances: FullAbundance[]): FullAbundance[] {
  return abundances.filter(abundance => abundance.measured || isNil(abundance.measured));
}

function buildProcessOverviewMetadata(
  siteOrAreaSamplesForTimePoint: FullSampleWithTime[],
  processMetadataContext: ProcessMetadataContext | undefined,
): Pick<OverviewDatum, 'processId' | 'processStepPointIds' | 'processFlowIds' | 'processStepSummaries'> | Record<string, never> {
  if (!processMetadataContext) {
    return {};
  }

  const processId = resolveProcessId(siteOrAreaSamplesForTimePoint, processMetadataContext);
  if (!processId) {
    return {};
  }

  const process = processMetadataContext.processById[processId];
  if (!process) {
    return {};
  }

  const processStepPointIds = uniq(process.samplingPointIds ?? []);
  const processFlowIds = uniq((process.flows ?? []).map(flow => flow.id));
  const samplesByOriginalSamplingPointId: Record<number, FullSampleWithTime[] | undefined> = chain(
    siteOrAreaSamplesForTimePoint,
  )
    .groupBy(sample => getOriginalSamplingPointId(sample.samplingPoint) ?? sample.samplingPoint.id)
    .value();

  const processStepSummaries: ProcessStepSummary[] = processStepPointIds
    .map(stepPointId => {
      const stepSamples = samplesByOriginalSamplingPointId[stepPointId] ?? [];
      if (!stepSamples.length) {
        return undefined;
      }
      const firstStepSample = stepSamples[0];
      const stepAbundances = stepSamples.flatMap(sample => getMeasuredAbundances(sample.abundances));
      const beforeAbundances = isAfterSample(firstStepSample) ? [] : stepAbundances;
      const afterAbundances = isAfterSample(firstStepSample) ? stepAbundances : [];
      const stepSamplingPoint = processMetadataContext.samplingPointById[stepPointId] ?? firstStepSample.samplingPoint;
      const name = stepSamplingPoint.name;
      const subtype = stepSamplingPoint.subtype ?? null;

      return {
        samplingPointId: stepPointId,
        name,
        subtype,
        beforeAbundances,
        afterAbundances,
      };
    })
    .filter((stepSummary): stepSummary is ProcessStepSummary => !!stepSummary);

  return {
    processId,
    processStepPointIds,
    processFlowIds,
    processStepSummaries,
  };
}

function resolveProcessId(
  siteOrAreaSamplesForTimePoint: FullSampleWithTime[],
  processMetadataContext: ProcessMetadataContext,
): number | undefined {
  if (!siteOrAreaSamplesForTimePoint.length) {
    return undefined;
  }

  const firstSample = siteOrAreaSamplesForTimePoint[0];
  const pooledSamplingPoint = firstSample.samplingPoint as PooledSplPntHack;
  if (processMetadataContext.isProcessGroupLevel && pooledSamplingPoint.poolingKey?.startsWith('process-')) {
    const processId = Number.parseInt(pooledSamplingPoint.poolingKey.replace('process-', ''), 10);
    return Number.isFinite(processId) ? processId : undefined;
  }

  const candidateProcessIds = uniq(
    siteOrAreaSamplesForTimePoint
      .map(sample => getOriginalSamplingPointId(sample.samplingPoint) ?? sample.samplingPoint.id)
      .map(samplingPointId => processMetadataContext.processIdBySamplingPointId[samplingPointId])
      .filter((id): id is number => typeof id === 'number'),
  );

  if (candidateProcessIds.length !== 1) {
    return undefined;
  }
  return candidateProcessIds[0];
}

function keepSelectedSamplingPoints(s: FullSampleWithTime, selectedSamplingPointIds: Set<number>) {
  const originalSamplingPointId = getOriginalSamplingPointId(s.samplingPoint);
  safeAssert(!!originalSamplingPointId, `Unexpectedly missing originalSamplingPointId`);
  return selectedSamplingPointIds.has(originalSamplingPointId);
}

function getSnappedLocalTimeFields({ time }: Pick<FullSampleWithTime, 'time'>) {
  const snappedTime = ensureLocalStartOfMonth(time);
  const formattedTime = friendlyMonth(snappedTime, 'postfixAlways');
  return { snappedTime: snappedTime.toISOString(), formattedTime };
}
